import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import SchoolProgressStepper from '../../../components/School/SchoolProgressStepper';
import useSchool, { SchoolProvider } from '../../../hooks/useSchool';
import SchoolSetupForm from './SchoolSetupForm';
import SchoolDashboard from './SchoolDashboard';
import SchoolEnsembles from './SchoolEnsembles';
import SchoolConfirmation from './SchoolConfirmation';
import RegistrationClosed from '../RegistrationClosed';
import Layout from '../../../components/Layout/Layout';
import { Alert, Center, Loader, Text } from '@mantine/core';
import { IconLock, IconClockHour4, IconMailX } from '@tabler/icons-react';
import { getActiveSeason, isRegistrationOpenForUser, CONTACT_EMAIL } from '../../../config/seasons';

const SchoolPortal = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    school, currentSeason, loading: schoolLoading,
    hasSchool, isPendingReview, isRejected,
  } = useSchool();

  // Redirect to login if not authenticated. An account has to exist before a
  // school can — every RLS policy here keys on auth.uid().
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/register/school');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || schoolLoading) {
    return (
      <Layout>
        <Center style={{ minHeight: '60vh' }}>
          <div style={{ textAlign: 'center' }}>
            <Loader color="var(--primary-gold)" size="lg" />
            <Text mt="md" color="dimmed">Loading studio portal...</Text>
          </div>
        </Center>
      </Layout>
    );
  }

  if (!user) return null;

  // Step 1 covers both "no school yet" and "school exists but hasn't signed
  // up for this season" — the returning-teacher state.
  // Steps 2–4 follow the page you're on: Students, Ensembles, Confirmation.
  const currentStep = !hasSchool || !currentSeason
    ? 1
    : currentSeason.is_finalized || location.pathname.endsWith('/confirmation')
      ? 4
      : location.pathname.endsWith('/ensembles')
        ? 3
        : 2;

  // The active season, not the school's latest one: a returning school has no
  // row for the new year until it signs up, and must not be shown last year's
  // closed season instead.
  const seasonYear = currentSeason?.competition_year || getActiveSeason().year;
  const seasonOpen = isRegistrationOpenForUser(seasonYear, user.email);

  // Review is advisory and never gates anything — a pending school registers
  // students exactly like an approved one.
  const reviewBanner = isRejected ? (
    <Alert
      icon={<IconMailX size={18} />}
      color="red"
      variant="light"
      radius={0}
      title="This studio account needs attention"
    >
      <Text size="sm">
        We weren't able to approve {school?.name} for this season. Please email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>
          {CONTACT_EMAIL}
        </a>{' '}
        and we'll sort it out with you.
      </Text>
    </Alert>
  ) : isPendingReview ? (
    <Alert
      icon={<IconClockHour4 size={18} />}
      color="blue"
      variant="light"
      radius={0}
      title="Your studio is being reviewed"
    >
      <Text size="sm">
        Nothing to do — our team is confirming your details and will be in touch about your invoice.
        You can add students and upload music in the meantime.
      </Text>
    </Alert>
  ) : null;

  if (!seasonOpen) {
    if (!hasSchool) {
      return <RegistrationClosed variant="school_no_acct" year={seasonYear} />;
    }
    // Teacher has a school → read-only run of the full flow so they can still
    // see their roster and what was submitted.
    return (
      <Layout>
        <Alert
          icon={<IconLock size={18} />}
          color="yellow"
          variant="light"
          radius={0}
          title={`Read-only view · ${seasonYear} registration closed`}
        >
          <Text size="sm">
            To update any student info, upload music, or make other changes, please email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>
              {CONTACT_EMAIL}
            </a>.
          </Text>
        </Alert>
        <SchoolProgressStepper currentStep={currentStep} />
        <Routes>
          <Route path="setup"        element={<SchoolSetupForm readOnly />} />
          <Route path="students"     element={<SchoolDashboard readOnly />} />
          <Route path="ensembles"    element={<SchoolEnsembles readOnly />} />
          <Route path="confirmation" element={<SchoolConfirmation readOnly />} />
          <Route path="*"            element={<Navigate to="/register/school/students" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout>
      {reviewBanner}
      <SchoolProgressStepper currentStep={currentStep} />
      <Routes>
        <Route path="setup" element={<SchoolSetupForm />} />
        <Route path="students" element={
          currentSeason
            ? <SchoolDashboard />
            : <Navigate to="/register/school/setup" replace />
        } />
        <Route path="ensembles" element={
          currentSeason
            ? <SchoolEnsembles />
            : <Navigate to="/register/school/setup" replace />
        } />
        <Route path="confirmation" element={
          currentSeason
            ? <SchoolConfirmation />
            : <Navigate to="/register/school/setup" replace />
        } />
        <Route path="*" element={
          !currentSeason
            ? <Navigate to="/register/school/setup" replace />
            : currentSeason.is_finalized
              ? <Navigate to="/register/school/confirmation" replace />
              : <Navigate to="/register/school/students" replace />
        } />
      </Routes>
    </Layout>
  );
};

// One shared copy of the school + seasons for every step of the portal.
const SchoolRegistration = () => (
  <SchoolProvider>
    <SchoolPortal />
  </SchoolProvider>
);

export default SchoolRegistration;
